/**
 * Export column picker.
 *
 * Reads the shared column registry, so the picker and the CSV writer can never
 * disagree about which columns exist or what order they come in. Grouped by
 * domain because 30+ flat checkboxes are unscannable.
 *
 * This controls the DOWNLOAD ONLY — the on-screen table columns are fixed. The
 * label, the count in the trigger, and the footer note all say so explicitly: a
 * control called just "Columns" sitting above a table reads as if it should
 * change the table, and then appears broken when it doesn't.
 */

import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Columns3, ChevronDown, Info } from 'lucide-react'
import { columnsFor, defaultColumnsFor, groupColumns, type ReportResource } from '@limo/shared'

interface Props {
  readonly resource: ReportResource
  readonly selected: readonly string[]
  readonly onChange: (next: readonly string[]) => void
}

export function ColumnPicker({ resource, selected, onChange }: Props) {
  const all = columnsFor(resource)
  const grouped = groupColumns(all)

  const toggle = (key: string): void => {
    onChange(selected.includes(key) ? selected.filter((k) => k !== key) : [...selected, key])
  }

  const toggleGroup = (keys: readonly string[], allOn: boolean): void => {
    onChange(allOn ? selected.filter((k) => !keys.includes(k)) : [...new Set([...selected, ...keys])])
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2" title="Choose which columns the CSV download contains">
          <Columns3 className="h-3.5 w-3.5" />
          <span>Export columns</span>
          <span className="text-xs text-muted-foreground">({selected.length})</span>
          <ChevronDown className="h-3.5 w-3.5 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="end">
        <div className="flex items-center justify-between border-b px-3 py-2">
          <span className="text-sm font-medium">Export columns</span>
          <div className="flex gap-1">
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs"
              onClick={() => onChange(all.map((c) => c.key))}
            >
              All
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs"
              onClick={() => onChange([...defaultColumnsFor(resource)])}
            >
              Reset
            </Button>
          </div>
        </div>

        <div className="max-h-[420px] overflow-y-auto px-3 py-2">
          {grouped.map(([group, columns]) => {
            const keys = columns.map((c) => c.key)
            const allOn = keys.every((k) => selected.includes(k))

            return (
              <div key={group} className="mb-3 last:mb-0">
                <button
                  type="button"
                  onClick={() => toggleGroup(keys, allOn)}
                  className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground hover:text-foreground"
                >
                  {group}
                </button>
                <div className="space-y-1.5">
                  {columns.map((column) => (
                    <label
                      key={column.key}
                      className="flex cursor-pointer items-center gap-2 text-sm"
                    >
                      <Checkbox
                        checked={selected.includes(column.key)}
                        onCheckedChange={() => toggle(column.key)}
                        aria-label={column.label}
                      />
                      <span>{column.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            )
          })}
        </div>

        <div className="space-y-1 border-t px-3 py-2 text-xs text-muted-foreground">
          <p>
            {selected.length} of {all.length} columns selected
            {selected.length === 0 && (
              // An empty selection would produce a headerless, dataless file.
              <span className="ml-1 text-destructive">— pick at least one to export</span>
            )}
          </p>
          {/*
            States the scope plainly. Without it, ticking boxes here looks like a
            no-op: the change is only visible inside the downloaded file.
          */}
          <p className="flex items-start gap-1.5">
            <Info className="mt-0.5 h-3 w-3 shrink-0" />
            <span>
              Applies to the CSV download only. The table columns above don&apos;t change.
              Saved with a view, and remembered on this device.
            </span>
          </p>
        </div>
      </PopoverContent>
    </Popover>
  )
}
